import { CreditsConfig } from '../model/credits/credits-config';
import { PricingMethodType } from '../model/credits/pricing-method-type';
import { Gym, GymType, MembershipType, NetworkType } from '../model/gym';
import { AttributeChangeSchedule } from '../model/schedule/attribute-change-schedule';
import { GymFieldType } from '../model/schedule/gym-field-type';

export type MembershipTypeTarget =
  | MembershipType.CREDITS
  | MembershipType.UNLIMITED;

export const MIN_CREDITS_PRICE = 0;
export const MAX_CREDITS_PRICE = 9999;

/**
 * FR/BE gyms must be either CREDITS (GYMLIB) or UNLIMITED (WELLPASS_FRANCE), never both.
 * A MEMBERSHIP_TYPE schedule swaps them atomically on the backend and sets/clears the
 * credits configuration. PLUS_1 (WELLPASS_FR_BE_PLUS1) is never touched.
 */
export class MembershipTypeScheduleUtils {
  public static isAvailableFor(gym?: Gym): boolean {
    return gym?.network === NetworkType.FR_BE;
  }

  public static canPromoteTo(
    gym: Gym | undefined,
    target: MembershipTypeTarget
  ): boolean {
    if (!gym || !this.isAvailableFor(gym)) {
      return false;
    }
    return (
      !this.currentMembershipTypes(gym).includes(target) ||
      this.hasConflict(gym)
    );
  }

  public static currentMembershipTypes(gym: Gym): MembershipType[] {
    return this.toMembershipTypes(gym.options?.gymTypes ?? []);
  }

  public static targetMembershipTypes(
    gym: Gym,
    target: MembershipTypeTarget
  ): MembershipType[] {
    return this.toMembershipTypes(
      this.targetGymTypes(gym.options?.gymTypes ?? [], target)
    );
  }

  public static targetGymTypes(
    gymTypes: GymType[],
    target: MembershipTypeTarget
  ): GymType[] {
    const toRemove =
      target === MembershipType.CREDITS
        ? GymType.WELLPASS_FR_BE_UNLIMITED
        : GymType.WELLPASS_FR_BE_CREDITS;
    const toAdd =
      target === MembershipType.CREDITS
        ? GymType.WELLPASS_FR_BE_CREDITS
        : GymType.WELLPASS_FR_BE_UNLIMITED;
    const result = gymTypes.filter(t => t !== toRemove && t !== toAdd);
    return [...result, toAdd];
  }

  public static hasConflict(gym: Gym): boolean {
    const types = this.currentMembershipTypes(gym);
    return (
      types.includes(MembershipType.CREDITS) &&
      types.includes(MembershipType.UNLIMITED)
    );
  }

  public static hasPlus1(gym: Gym): boolean {
    return (gym.options?.gymTypes ?? []).includes(GymType.WELLPASS_FR_BE_PLUS1);
  }

  public static currentPrices(gym: Gym): number[] {
    return (gym.creditsConfig?.pricing ?? []).map(p => p.price);
  }

  public static isValidPrice(price: unknown): price is number {
    return (
      typeof price === 'number' &&
      Number.isInteger(price) &&
      price >= MIN_CREDITS_PRICE &&
      price <= MAX_CREDITS_PRICE
    );
  }

  public static buildCreditsConfig(price: number): CreditsConfig {
    return {
      pricing: [
        {
          price,
          priority: 0,
          pricingMethod: { type: PricingMethodType.STANDARD },
        },
      ],
    };
  }

  public static buildSchedule(
    gymId: string,
    target: MembershipTypeTarget,
    scheduledAt: string,
    price?: number
  ): AttributeChangeSchedule {
    if (target === MembershipType.CREDITS && !this.isValidPrice(price)) {
      throw new Error(
        `A credits price between ${MIN_CREDITS_PRICE} and ${MAX_CREDITS_PRICE} is required`
      );
    }
    return {
      gymId,
      field: GymFieldType.MEMBERSHIP_TYPE,
      name: target,
      value:
        target === MembershipType.CREDITS
          ? JSON.stringify(this.buildCreditsConfig(price as number))
          : 'true',
      scheduledAt,
    };
  }

  public static displayName(schedule: AttributeChangeSchedule): string {
    if (schedule.field !== GymFieldType.MEMBERSHIP_TYPE) {
      return schedule.name ?? '';
    }
    return schedule.name === MembershipType.CREDITS
      ? 'Promote to Credits'
      : schedule.name === MembershipType.UNLIMITED
        ? 'Promote to Unlimited'
        : (schedule.name ?? '');
  }

  public static displayValue(schedule: AttributeChangeSchedule): string {
    if (schedule.field !== GymFieldType.MEMBERSHIP_TYPE) {
      return schedule.value ?? '';
    }
    if (schedule.name === MembershipType.UNLIMITED) {
      return 'Credits configuration removed';
    }
    try {
      const config = JSON.parse(schedule.value ?? '') as CreditsConfig;
      const prices = (config.pricing ?? []).map(p => p.price);
      if (prices.length > 0) {
        return `${prices.join(', ')} ${prices.length === 1 && prices[0] === 1 ? 'credit' : 'credits'}`;
      }
    } catch {
      // fall through to the raw value
    }
    return schedule.value ?? '';
  }

  private static toMembershipTypes(gymTypes: GymType[]): MembershipType[] {
    const result: MembershipType[] = [];
    if (gymTypes.includes(GymType.WELLPASS_FR_BE_UNLIMITED)) {
      result.push(MembershipType.UNLIMITED);
    }
    if (gymTypes.includes(GymType.WELLPASS_FR_BE_CREDITS)) {
      result.push(MembershipType.CREDITS);
    }
    if (gymTypes.includes(GymType.WELLPASS_FR_BE_PLUS1)) {
      result.push(MembershipType.PLUS_1);
    }
    return result;
  }
}
